import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type Aal = "aal1" | "aal2" | null;

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  /** Current effective Authenticator Assurance Level on this session. */
  currentLevel: Aal;
  /** Required AAL for full access (= aal2 if user has any verified MFA factor). */
  nextLevel: Aal;
  /** True iff session is `aal1` but the user enrolled MFA → must pass challenge. */
  mfaRequired: boolean;
  /** Re-read AAL after enroll / verify / unenroll. */
  refreshAal: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  loading: true,
  currentLevel: null,
  nextLevel: null,
  mfaRequired: false,
  refreshAal: async () => {},
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [currentLevel, setCurrentLevel] = useState<Aal>(null);
  const [nextLevel, setNextLevel] = useState<Aal>(null);
  // True once AAL has been read at least once for the current session.
  // Until this is true we MUST treat `loading` as true so RequireAuth
  // does not let an aal1 user past the gate before we know whether they
  // have an MFA factor enrolled.
  const [aalLoaded, setAalLoaded] = useState(false);
  // Guard against overlapping AAL fetches (auth events can fire in bursts).
  const aalSeq = useRef(0);
  // Last known signed-in user id. Used to tell a genuine (re)authentication
  // apart from a background token refresh that re-emits for the same user.
  const lastUserId = useRef<string | null>(null);

  const readAal = useCallback(async () => {
    const seq = ++aalSeq.current;
    try {
      const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (seq !== aalSeq.current) return; // stale
      if (error) {
        setCurrentLevel(null);
        setNextLevel(null);
      } else {
        setCurrentLevel((data?.currentLevel as Aal) ?? null);
        setNextLevel((data?.nextLevel as Aal) ?? null);
      }
    } catch {
      if (seq !== aalSeq.current) return;
      setCurrentLevel(null);
      setNextLevel(null);
    } finally {
      if (seq === aalSeq.current) setAalLoaded(true);
    }
  }, []);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, nextSession) => {
      // A background token refresh (or a same-user re-emit) keeps the existing
      // session valid: the user stays logged in. We must NOT flip `user` to null
      // or force the loading gate here, otherwise RequireAuth unmounts the whole
      // page (including open modals) and remounts it, wiping transient state.
      if (event === "TOKEN_REFRESHED" || event === "USER_UPDATED") {
        setSession(nextSession);
        if (nextSession?.user) setUser(nextSession.user);
        return;
      }

      // Genuine auth transitions (SIGNED_IN, SIGNED_OUT, INITIAL_SESSION, ...).
      setSession(nextSession);
      const nextUser = nextSession?.user ?? null;
      setUser(nextUser);
      if (nextUser) {
        // Only force the AAL gate on a real (re)authentication — i.e. when the
        // user identity actually changes — never on a re-emit for the same user.
        if (lastUserId.current !== nextUser.id) {
          lastUserId.current = nextUser.id;
          setAalLoaded(false);
        }
        setTimeout(() => { void readAal(); }, 0);
      } else {
        lastUserId.current = null;
        setCurrentLevel(null);
        setNextLevel(null);
        setAalLoaded(true);
      }
      setLoading(false);
    });

    supabase.auth.getSession().then(({ data: { session: initial } }) => {
      setSession(initial);
      const initialUser = initial?.user ?? null;
      setUser(initialUser);
      if (initialUser) {
        lastUserId.current = initialUser.id;
        setAalLoaded(false);
        void readAal();
      } else {
        setAalLoaded(true);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [readAal]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setCurrentLevel(null);
    setNextLevel(null);
    setAalLoaded(true);
  };

  // mfaRequired is the canonical "you must pass MFA before continuing" flag.
  // True only when the user is signed in (aal1) AND has at least one verified
  // factor (so nextLevel becomes aal2).
  const mfaRequired = !!user && currentLevel === "aal1" && nextLevel === "aal2";

  // Block routes until we know the AAL state for a signed-in user.
  const effectiveLoading = loading || (!!user && !aalLoaded);

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading: effectiveLoading,
        currentLevel,
        nextLevel,
        mfaRequired,
        refreshAal: readAal,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
